import { useEffect } from 'react'
import {
  productGA,
  customerGA,
  customerHeap,
  formatMoney,
} from '@/helpers'

import {
  Cart,
  MarketingChannels,
  Facebook,
  Listrak
} from '@/services'

import { useProduct } from '@/hooks/useProduct'
import {
  usePrevious,
} from '@/hooks'
import { getCustomer, GraphQlCustomer } from '@/helpers/graphql'
import { BigCommerceCart } from '@/types/Cart'
import { error, log } from '@/services/Log'

declare const window: Window & typeof globalThis & {
  addthis?,
  populatePromoBanner?,
  populateMegaNav?,
}


interface PdpTrackingServicesProps {
  children: React.ReactNode | React.ReactNode[],
  user: GraphQlCustomer | null,
  cart: BigCommerceCart | null
}

const PdpTrackingServices: React.FC<PdpTrackingServicesProps> = ({
  children,
  user,
  cart,
}) => {
  const {
    product,
    sku,
    price,
    calculatedPrice: fallBackPrice,
    variantId: selectedVariantId,
    selectedVariant,
    setCart,
  } = useProduct()

  const variantPrice = product?.variants?.find((v) => v.sku === sku)?.calculated_price
  const calculatedPrice = formatMoney(variantPrice) || fallBackPrice || 'Not available'

  const previousSku = usePrevious(sku)

  // Event handlers

  useEffect(() => {
    const mount = async () => {
      const newCart = await Cart.content()
      setCart(newCart)
      const cartTotal = newCart?.cartAmount.toFixed(2)
      customerGA(cartTotal, 'product')
      customerHeap(cartTotal, 'product')
    }
    if (sku && !previousSku) {
      mount()
    }
  }, [product, sku])

  useEffect(() => {
    if (!sku || !selectedVariantId || !product) {
      return
    }

    const { name, category_info: categoryInfo, brand } = product
    const salePrice = parseFloat(`${calculatedPrice}`.replace(/(^\$|\,)/g, ''))
    productGA({
      name,
      sku,
      price,
      salePrice,
      variantId: selectedVariantId,
      categories: categoryInfo,
      event: 'productDetailView',
      brand,
      quantity: [...(cart?.lineItems?.physicalItems || []), ...(cart?.lineItems?.digitalItems || [])]?.find(i => i?.sku === sku)?.quantity || 0,
      customerId: user?.entityId,
      customerEmail: user?.email,
      cartTotal: cart?.cartAmount || 0
    })
    MarketingChannels.onSkuChange(name, categoryInfo, selectedVariantId, calculatedPrice)
    Listrak.listrakProductBrowse(sku)
  }, [sku, selectedVariantId, product, user, cart])

  useEffect(() => {
    const reportViewContentToFacebook = async () => {
      if (!sku || !selectedVariant || !product) {
        return
      }
      const { name } = selectedVariant
      const { category_info: categoryInfo } = product
      const salePrice = parseFloat(`${calculatedPrice}`.replace(/(^\$|\,)/g, ''))
      const { customer } = await getCustomer()
      const ViewContentpayload = {
        user: customer,
        content_ids: [sku],
        content_category: [...categoryInfo].pop()?.name || '',
        content_name: name,
        contents: [{ id: sku, quantity: 1, item_price: salePrice }],
        value: salePrice,
      }
      log(`Facebook.viewContent`, { ViewContentpayload })
      Facebook.viewContent(ViewContentpayload)
    }
    reportViewContentToFacebook()
  }, [sku, selectedVariant, calculatedPrice])

  useEffect(() => {
    if (typeof window.addthis !== 'undefined' && typeof window.addthis.layers.refresh === 'function') {
      window.addthis.layers.refresh()
    }

    if (typeof window !== 'undefined' && typeof window?.populatePromoBanner === 'function') {
      try {
        window.populatePromoBanner()
      } catch (promoBannerError) {
        error(`
        WARNING - PROMOBANNER ERROR:
        ${promoBannerError}
          ${window.populatePromoBanner.toString()}
        `)
      }
    }

    if (typeof window !== 'undefined' && typeof window?.populateMegaNav === 'function') {
      try {
        window.populateMegaNav()
      } catch (meganavError) {
        error(`
          WARNING - MEGANAV ERROR:
          ${meganavError}
          ${window.populateMegaNav.toString()}
        `)
      }
    }
  }, [])

  return (
    <>
      {children}
    </>
  )
}

export default PdpTrackingServices
